import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGamepad, FaWallet, FaCoins, FaLevelUpAlt, FaRobot, FaArrowRight, FaBullhorn } from 'react-icons/fa';
import { GiMinerals, GiCyberEye, GiGalaxy } from 'react-icons/gi';
import { BiCube } from 'react-icons/bi';
import './Games.scss';
import mascotImage from '../../../assets/reddickrednobackground.png';
import gamePreviewImage from '../../../assets/reddickofficeexample.png';
import featureIconRadix from '../../../assets/radix-icon.svg';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const Games = () => {
  const navigate = useNavigate();

  const [previewRef, previewVisible] = useScrollReveal(0.08);
  const [mechanicsRef, mechanicsVisible] = useScrollReveal(0.05);
  const [whyRef, whyVisible] = useScrollReveal(0.08);
  const [ctaRef, ctaVisible] = useScrollReveal(0.08);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRedirect = () => navigate('/start');

  return (
    <div className="gp-page" data-scroll-root>
      <Header />

      <main className="gp-content">
        <section className="gp-hero">
          <div className="gp-hero__bg">
            <div className="gp-hero__orb gp-hero__orb--1"></div>
            <div className="gp-hero__orb gp-hero__orb--2"></div>
            <div className="gp-hero__orb gp-hero__orb--3"></div>
          </div>
          <div className="container">
            <div className="gp-hero__wrapper">
              <span className="gp-hero__tagline">Play-to-Earn on Radix</span>
              <h1 className="gp-hero__title">
                <span className="gp-hero__title-line">REDDICKS</span>
                <span className="gp-hero__title-badge">P2E</span>
              </h1>

              <div className="gp-hero__mascot">
                <div className="gp-hero__mascot-glow"></div>
                <img src={mascotImage} alt="Reddicks Mascot - Ready to Play!" className="gp-hero__mascot-img" />
              </div>

              <p className="gp-hero__subtitle">Step Into Your 3D Cyberpunk Office & Build Your Mining Empire</p>

              <div className="gp-hero__actions">
                <button className="gp-cta gp-cta--primary" onClick={handleRedirect}>
                  Enter the Game <FaArrowRight />
                </button>
              </div>

              <div className="gp-announcement">
                <div className="gp-announcement__glow"></div>
                <div className="gp-announcement__content">
                  <div className="gp-announcement__icon">
                    <FaBullhorn />
                  </div>
                  <div className="gp-announcement__text">
                    <h3 className="gp-announcement__title">3D Game Rewards Are Now DCKSLAP</h3>
                    <p className="gp-announcement__desc">
                      The 3D game now pays rewards in <strong>$DCKSLAP</strong>.
                      Your rigs still run the same way, and the live reward pool distributes <strong>40 DCKSLAP per day</strong> to eligible miners.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="gp-hero__scroll">
            <span></span>
          </div>
        </section>

        <section ref={previewRef} className={`gp-preview ${previewVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Your Cyberpunk Office Awaits</h2>
            <div className="gp-preview__grid">
              <div className="gp-preview__image-card">
                <img src={gamePreviewImage} alt="Reddicks 3D Cyberpunk Office" className="gp-preview__img" />
                <div className="gp-preview__overlay">
                  <div className="gp-preview__indicator">
                    <FaGamepad className="gp-preview__indicator-icon" />
                    <span>Interactive 3D Environment</span>
                  </div>
                </div>
              </div>

              <div className="gp-preview__info">
                <h3>Immersive Browser-Based Gaming</h3>
                <p>Enter a fully realized <strong>3D cyberpunk office space</strong> that runs directly in your browser using WebGL technology. No downloads, no installations - just pure gaming experience.</p>

                <div className="gp-preview__feature">
                  <div className="gp-preview__feature-icon gp-preview__feature-icon--cyan">
                    <GiCyberEye />
                  </div>
                  <div className="gp-preview__feature-text">
                    <h4>Real-Time 3D Environment</h4>
                    <p>Navigate your cyberpunk office with WASD controls, complete with atmospheric lighting, interactive objects, and immersive sound design.</p>
                  </div>
                </div>

                <div className="gp-preview__feature">
                  <div className="gp-preview__feature-icon gp-preview__feature-icon--violet">
                    <BiCube />
                  </div>
                  <div className="gp-preview__feature-text">
                    <h4>Mining Rig Management</h4>
                    <p>Place and manage up to 4 different tiers of mining rigs in your office space. Watch them generate passive DCKSLAP income on the live payout schedule.</p>
                  </div>
                </div>

                <div className="gp-preview__feature">
                  <div className="gp-preview__feature-icon gp-preview__feature-icon--green">
                    <FaWallet />
                  </div>
                  <div className="gp-preview__feature-text">
                    <h4>Direct Wallet Integration</h4>
                    <p>Experience true ownership. Your in-game balances and DCKSLAP withdrawals are directly linked to your Radix wallet for maximum security and control.</p>
                  </div>
                </div>

                <button className="gp-cta gp-cta--accent" onClick={handleRedirect}>
                  Experience the Office <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section ref={mechanicsRef} className={`gp-mechanics ${mechanicsVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Core Gameplay Mechanics</h2>
            <div className="gp-mechanics__grid">
              <div className="gp-mechanics__card gp-mechanics__card--coral">
                <div className="gp-mechanics__card-header">
                  <div className="gp-mechanics__card-icon">
                    <GiMinerals />
                  </div>
                  <h3>Mining Operations</h3>
                </div>
                <div className="gp-mechanics__card-body">
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">01</span>
                    <div className="gp-mechanics__row-content">
                      <strong>4 Rig Tiers:</strong> From basic (10 power) to advanced (40 power) mining rigs
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">02</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Passive Income:</strong> Earn proportional DCKSLAP rewards every 5 minutes based on your setup
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">03</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Efficiency Multipliers:</strong> Higher tiers provide better efficiency (1x to 2x)
                    </div>
                  </div>
                </div>
              </div>

              <div className="gp-mechanics__card gp-mechanics__card--green">
                <div className="gp-mechanics__card-header">
                  <div className="gp-mechanics__card-icon">
                    <FaCoins />
                  </div>
                  <h3>Token Economics</h3>
                </div>
                <div className="gp-mechanics__card-body">
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">01</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Daily Pool:</strong> 40 DCKSLAP distributed across all miners daily
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">02</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Payout Cadence:</strong> Rewards are written every 5 minutes by the live backend job
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">03</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Fair Distribution:</strong> Rewards scale with your share of total eligible mining power
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">04</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Mining Requirement:</strong> Minimum 15,000 $REDDICKS in wallet to earn rewards
                    </div>
                  </div>
                </div>
              </div>

              <div className="gp-mechanics__card gp-mechanics__card--violet">
                <div className="gp-mechanics__card-header">
                  <div className="gp-mechanics__card-icon">
                    <FaLevelUpAlt />
                  </div>
                  <h3>Progression System</h3>
                </div>
                <div className="gp-mechanics__card-body">
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">01</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Start with 10:</strong> Begin your journey with DCKSLAP starting capital
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">02</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Upgrade Path:</strong> Reinvest earnings into powerful rigs (costs: 10, 20, 40, 80 DCKSLAP)
                    </div>
                  </div>
                  <div className="gp-mechanics__row">
                    <span className="gp-mechanics__row-num">03</span>
                    <div className="gp-mechanics__row-content">
                      <strong>Leaderboards:</strong> Compete with other miners for top earnings
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={whyRef} className={`gp-why ${whyVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Why Choose Reddicks P2E?</h2>
            <div className="gp-why__grid">
              <div className="gp-why__card">
                <div className="gp-why__card-icon gp-why__card-icon--green">
                  <FaCoins />
                </div>
                <h3>Real Token Rewards</h3>
                <p>Earn actual DCKSLAP tokens that can be withdrawn to your Radix wallet, while keeping the 15,000 REDDICKS holder gate for mining eligibility.</p>
              </div>
              <div className="gp-why__card">
                <div className="gp-why__card-icon gp-why__card-icon--violet">
                  <GiGalaxy />
                </div>
                <h3>Cutting-Edge Tech</h3>
                <p>Built with Three.js and WebGL for smooth 3D performance. Experience console-quality graphics in your browser.</p>
              </div>
              <div className="gp-why__card">
                <div className="gp-why__card-icon gp-why__card-icon--cyan">
                  <FaRobot />
                </div>
                <h3>Vibrant Community</h3>
                <p>Join a thriving ecosystem of miners, traders, and cyberpunk enthusiasts building the future of Web3 gaming.</p>
              </div>
              <div className="gp-why__card">
                <div className="gp-why__card-icon gp-why__card-icon--crimson">
                  <img src={featureIconRadix} alt="Radix Network Icon" className="gp-why__radix-img" />
                </div>
                <h3>Powered by Radix</h3>
                <p>Built on the most advanced DeFi platform with instant transactions and enterprise-grade security.</p>
              </div>
            </div>
          </div>
        </section>

        <section ref={ctaRef} className={`gp-final ${ctaVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="gp-final__bg">
            <div className="gp-final__orb gp-final__orb--1"></div>
            <div className="gp-final__orb gp-final__orb--2"></div>
          </div>
          <div className="container">
            <h2 className="section-title centered">Ready to Build Your Empire?</h2>
            <p className="gp-final__desc">Step into the future of Play-to-Earn gaming with Reddicks. Build your mining operation, earn DCKSLAP, and become part of the cyberpunk revolution.</p>
            <div className="gp-final__actions">
              <button className="gp-cta gp-cta--launch" onClick={handleRedirect}>
                Launch Your Career <FaArrowRight />
              </button>
            </div>
            <div className="gp-final__mascot">
              <img src={mascotImage} alt="Reddicks flying by" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Games;
